import React from 'react';
import { toast } from 'sonner';

const formatBetaDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const StatusBadge = ({ status }) => {
  const styles = {
    active: 'bg-green-500/20 text-green-400 border-green-500/30',
    used: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    revoked: 'bg-red-500/20 text-red-400 border-red-500/30'
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${styles[status] || 'bg-gray-500/20 text-gray-400'}`}>
      {status}
    </span>
  );
};

export const BetaCodesSection = ({ codes, codeFilter, setCodeFilter, generateQty, assignees, generating, updateQuantity, updateAssignee, handleGenerate, handleRevoke }) => {
  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Code copied to clipboard');
  };

  return (
    <div className="space-y-4">
      {/* Generate Form */}
      <div className="bg-gray-900 rounded-lg p-4 border border-gray-700">
        <h3 className="text-white font-semibold mb-3">Generate Beta Codes</h3>
        <div className="flex flex-col sm:flex-row gap-3 items-end mb-4">
          <div className="w-full sm:w-32">
            <label className="block text-xs text-gray-400 mb-1">Quantity</label>
            <input data-testid="beta-generate-qty" type="number" min="1" max="100" value={generateQty}
              onChange={(e) => updateQuantity(e.target.value)} className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm" />
          </div>
          <button data-testid="beta-generate-btn" onClick={handleGenerate} disabled={generating}
            className="px-6 py-2 bg-green-500 hover:bg-green-600 disabled:opacity-50 text-white rounded-lg text-sm font-semibold transition whitespace-nowrap">
            {generating ? 'Generating...' : `Generate ${generateQty} Code${generateQty > 1 ? 's' : ''}`}
          </button>
        </div>
        <div className="space-y-2 max-h-64 overflow-y-auto">
          <label className="block text-xs text-gray-400">Assign each code to a recipient (optional)</label>
          {assignees.map((assignee, idx) => (
            <div key={`beta-${idx}`} className="flex items-center gap-2">
              <span className="text-xs text-gray-500 w-8 text-right shrink-0">{idx + 1}.</span>
              <input data-testid={`beta-assignee-${idx}`} type="text" placeholder="Name or email of recipient"
                value={assignee} onChange={(e) => updateAssignee(idx, e.target.value)}
                className="flex-1 px-3 py-1.5 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm placeholder-gray-500" />
            </div>
          ))}
        </div>
      </div>

      {/* Code Filter */}
      <div className="flex gap-2">
        {['all', 'active', 'used', 'revoked'].map((f) => (
          <button key={f} onClick={() => setCodeFilter(f)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${codeFilter === f ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`}>
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {/* Codes Table */}
      <div className="bg-gray-900 rounded-lg border border-gray-700 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-700">
              <th className="text-left px-4 py-3 text-gray-400 font-medium">Code</th>
              <th className="text-left px-4 py-3 text-gray-400 font-medium">Assigned To</th>
              <th className="text-left px-4 py-3 text-gray-400 font-medium">Status</th>
              <th className="text-left px-4 py-3 text-gray-400 font-medium">Used By</th>
              <th className="text-left px-4 py-3 text-gray-400 font-medium">Created</th>
              <th className="text-left px-4 py-3 text-gray-400 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {codes.length === 0 ? (
              <tr><td colSpan="6" className="text-center py-8 text-gray-500">No beta codes found</td></tr>
            ) : codes.map((c) => (
              <tr key={c.code} className="border-b border-gray-800 hover:bg-gray-800/50">
                <td className="px-4 py-3">
                  <button onClick={() => copyCode(c.code)} className="font-mono text-white hover:text-green-400 transition" title="Click to copy">{c.code}</button>
                </td>
                <td className="px-4 py-3 text-gray-300">{c.assigned_to || '-'}</td>
                <td className="px-4 py-3"><StatusBadge status={c.status} /></td>
                <td className="px-4 py-3 text-gray-300">{c.used_by || '-'}</td>
                <td className="px-4 py-3 text-gray-400">{formatBetaDate(c.created_at)}</td>
                <td className="px-4 py-3">
                  {c.status === 'active' && (
                    <button onClick={() => handleRevoke(c.code)} className="text-xs text-red-400 hover:text-red-300 transition font-medium">Revoke</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export const BetaUsersSection = ({ betaUsers, userFilter, setUserFilter }) => (
  <div className="space-y-4">
    <div className="flex gap-2">
      {['all', 'active', 'expired'].map((f) => (
        <button key={f} onClick={() => setUserFilter(f)}
          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${userFilter === f ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`}>
          {f.charAt(0).toUpperCase() + f.slice(1)}
        </button>
      ))}
    </div>
    <div className="bg-gray-900 rounded-lg border border-gray-700 overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-700">
            <th className="text-left px-4 py-3 text-gray-400 font-medium">Name</th>
            <th className="text-left px-4 py-3 text-gray-400 font-medium">Email</th>
            <th className="text-left px-4 py-3 text-gray-400 font-medium">Beta Code</th>
            <th className="text-left px-4 py-3 text-gray-400 font-medium">Registered</th>
            <th className="text-left px-4 py-3 text-gray-400 font-medium">Beta Expires</th>
            <th className="text-left px-4 py-3 text-gray-400 font-medium">Status</th>
            <th className="text-left px-4 py-3 text-gray-400 font-medium">Days Left</th>
          </tr>
        </thead>
        <tbody>
          {betaUsers.length === 0 ? (
            <tr><td colSpan="7" className="text-center py-8 text-gray-500">No beta users found</td></tr>
          ) : betaUsers.map((u, idx) => (
            <tr key={`${u.user_id}-${idx}`} className="border-b border-gray-800 hover:bg-gray-800/50">
              <td className="px-4 py-3 text-white font-medium">{u.name}</td>
              <td className="px-4 py-3 text-gray-300">{u.email}</td>
              <td className="px-4 py-3 font-mono text-gray-400 text-xs">{u.beta_code_used || '-'}</td>
              <td className="px-4 py-3 text-gray-400">{formatBetaDate(u.created_at)}</td>
              <td className="px-4 py-3 text-gray-400">{formatBetaDate(u.beta_expires_at)}</td>
              <td className="px-4 py-3">
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${u.beta_active ? 'bg-green-500/20 text-green-400 border-green-500/30' : 'bg-amber-500/20 text-amber-400 border-amber-500/30'}`}>
                  {u.beta_active ? 'Active' : 'Expired'}
                </span>
              </td>
              <td className="px-4 py-3 text-gray-300">{u.days_remaining}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);
